import { StudentApplication } from '../components/Application/types'
import { Application } from './applications'
import { useAuthQuery } from './auth'
import { getPassport, isPassportProcessingComplete, Passport } from './useGetPassport'

type GetApplicationPassportsHistoryProps = {
  application: StudentApplication
  setHasAiProcessingTimedOut: (hasAiProcessingTimedOut: boolean) => void
  configTimeout?: number
  refetchCondition?: (data: Passport | undefined) => boolean
  enabled?: boolean
}

export function useGetApplicationPassportsHistory(props: GetApplicationPassportsHistoryProps) {
  const { isLoading, isFetching, data } = useAuthQuery(
    ['applicationPassports', props.application.id],
    () => GetPassportHistory(props.application),
    {
      enabled: props.enabled,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchInterval: ({ state }) => {
        const configTimeout = props.configTimeout || 45000
        const elapsedTime = state.dataUpdatedAt > 0 ? Date.now() - state.dataUpdatedAt : 0
        const hasExceededMaxTime = state.dataUpdatedAt > 0 && elapsedTime > configTimeout

        if (hasExceededMaxTime) {
          props.setHasAiProcessingTimedOut(true)
          return false
        }

        if (isPassportProcessingComplete(state.data?.currentPassport)) {
          return false
        }
        return 5000
      },
    },
  )

  return {
    isLoadingApplicationPassportsHistory: isLoading,
    isFetchingApplicationPassportsHistory: isFetching,
    passportsHistory: data?.history ?? [],
    currentPassport: data?.currentPassport ?? null,
  }
}

export async function GetPassportHistory(application: Application): Promise<{
  history: Passport[]
  currentPassport: Passport
}> {
  const passportIds = Object.values(
    application.attributes?.statusAndCitizenship?.passports || {},
  ).map(p => p.passportId || '')
  const studentId = application.relationships?.student?.data.id
  // Make the parallel requests to fetch passports by their IDs
  const requests = passportIds.map(id => getPassport(studentId, id))
  const responses = await Promise.all(requests)
  // Filter out any undefined responses (in case some IDs were invalid)
  const validResponses = responses.filter(response => response && response.data)
  const passports = validResponses.reduce(
    (acc, response) => {
      if (response.data) {
        acc.data.push(response.data)
      }
      return acc
    },
    { data: [] as Passport[] },
  )
  const sortedPassports = passports.data.sort((a, b) => {
    const dateA = new Date(a.attributes.createdAt as string).getTime()
    const dateB = new Date(b.attributes.createdAt as string).getTime()
    return dateB - dateA // Sort by createdAt in descending order
  })
  
  return {
    history: sortedPassports,
    currentPassport: sortedPassports[0],
  }
}
